/**
 * Dialog.
 *
 * A scrim, an opaque panel, a trapped focus ring and one escape route that
 * always works. Panels are opaque in both directions: elevation is a surface
 * step plus a hairline, never blur and never glow.
 *
 * The focus contract, which is the part that gets skipped and then costs a
 * screen reader user the page:
 *   - opening moves focus to the element marked data-autofocus, or to the
 *     first focusable thing in the panel;
 *   - Tab and Shift Tab cycle inside the panel and never reach the page;
 *   - Escape closes, and so does a click on the scrim;
 *   - closing returns focus to whatever opened it.
 *
 * A destructive dialog passes dismissible and still gets Escape, because a way
 * out is not the thing to make hard. What is made hard is the confirm.
 */

import type { ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { captureEvent } from '../../lib/analytics/posthog';
import '../../styles/components/dialog.css';

const FOCUSABLE = [
  'a[href]',
  'button:not(:disabled)',
  'input:not(:disabled)',
  'select:not(:disabled)',
  'textarea:not(:disabled)',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** A question containing the object's real name, per screen spec 4.5. */
  title: string;
  /** Read out with the title. Keep it to one sentence. */
  description?: string;
  children?: ReactNode;
  /** The action row. Cancel sits on the left and takes focus first. */
  footer?: ReactNode;
  /** Rendered inside the page rather than a portal. Used by the kit gallery. */
  inline?: boolean;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  inline = false,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const returnTo = useRef<HTMLElement | null>(null);
  const openTimeRef = useRef<number>(0);
  const titleId = useId();
  const descId = useId();

  const focusables = useCallback((): HTMLElement[] => {
    const panel = panelRef.current;
    if (!panel) return [];
    return Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (node) => node.offsetParent !== null || node === document.activeElement,
    );
  }, []);

  useEffect(() => {
    if (!open || inline) return;

    openTimeRef.current = Date.now();
    captureEvent('dialog_opened', {
      title,
      screen_path: window.location.pathname,
    });

    returnTo.current = document.activeElement as HTMLElement | null;

    const panel = panelRef.current;
    const preferred = panel?.querySelector<HTMLElement>('[data-autofocus]');
    const first = focusables()[0];
    (preferred ?? first ?? panel)?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
      returnTo.current?.focus();
    };
  }, [open, inline, focusables, title]);

  const handleClose = useCallback((method = 'escape') => {
    const duration = Date.now() - openTimeRef.current;
    captureEvent('dialog_closed', {
      title,
      close_method: method,
      duration_ms: duration,
      screen_path: window.location.pathname,
    });
    onClose();
  }, [onClose, title]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handleClose('escape');
        return;
      }
      if (event.key !== 'Tab') return;

      const nodes = focusables();
      if (nodes.length === 0) {
        event.preventDefault();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === panelRef.current)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [focusables, onClose],
  );

  if (!open) return null;

  const body = (
    <div className="dialog" data-inline={inline ? 'yes' : 'no'}>
      <div
        className="dialog__scrim"
        onClick={() => handleClose('scrim')}
        role="presentation"
        data-testid="dialog-scrim"
      />
      <div
        className="dialog__panel"
        role="dialog"
        aria-modal={inline ? undefined : true}
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        ref={panelRef}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        <h2 className="dialog__title" id={titleId}>
          {title}
        </h2>
        {description ? (
          <p className="dialog__desc" id={descId}>
            {description}
          </p>
        ) : null}
        {children ? <div className="dialog__body">{children}</div> : null}
        {footer ? <div className="dialog__foot">{footer}</div> : null}
      </div>
    </div>
  );

  if (inline) return body;
  return createPortal(body, document.body);
}
